import { CommonModule } from '@angular/common';
import { Component, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Offcanvas } from 'bootstrap-italia';
import { combineLatest, Subject, takeUntil } from 'rxjs';

import { SidebarDataService } from 'src/app/services/sidebar-data.service';
import { StateManagementService } from 'src/app/services/state-management.service';
import { SIDEBAR_DROPDOWNS, DropdownKey } from './sidebar-left.config';

type SidebarVm = {
  regioni: string[];
  province: string[];
  enti: string[];
  reti: string[];
  activeFiltersCount: number;
  startDate: string;
  endDate: string;
};

type UiOption = { id: string; label: string; selected: boolean };

@Component({
  selector: 'app-sidebar-left',
  standalone: true,
  templateUrl: './sidebar-left.component.html',
  styleUrls: ['./sidebar-left.component.css'],
  imports: [CommonModule, FormsModule],
})
export class SidebarLeftComponent implements OnInit, AfterViewInit, OnDestroy {
  private offcanvasInstance!: Offcanvas;
  private destroy$ = new Subject<void>();

  globalSearch = '';

  dropdownConfig = SIDEBAR_DROPDOWNS;

  dropdownsOpen: Record<DropdownKey, boolean> = {
    regioni: false,
    province: false,
    enti: false,
    reti: false,
    dateRange: false,
  };

  regioni: UiOption[] = [];
  province: UiOption[] = [];
  enti: UiOption[] = [];
  reti: UiOption[] = [];

  vm: SidebarVm = {
    regioni: [],
    province: [],
    enti: [],
    reti: [],
    activeFiltersCount: 0,
    startDate: '',
    endDate: '',
  };

  constructor(
    private sidebarData: SidebarDataService,
    private stateChange: StateManagementService
  ) {
    this.setupVmListener();
  }

  ngOnInit(): void {
    if (!this.sidebarData.hasBaseData) {
      this.sidebarData.initLoadAll().pipe(takeUntil(this.destroy$)).subscribe();
    }

    this.sidebarData.sidebarData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(d => {
        this.regioni = this.toUiOptions(d.regioni, 'regioni');
        this.province = this.toUiOptions(d.province, 'province');
        this.enti = this.toUiOptions(d.enti, 'enti');
        this.reti = this.toUiOptions(d.reti, 'reti');
      });
  }

  ngAfterViewInit(): void {
    const el = document.getElementById('offcanvasExample');
    if (el) this.offcanvasInstance = new Offcanvas(el);
  }

  // =========================
  // DROPDOWN
  // =========================
  toggleDropdown(key: DropdownKey) {
    this.dropdownsOpen[key] = !this.dropdownsOpen[key];
  }

  isDropdownOpen(key: DropdownKey): boolean {
    return this.dropdownsOpen[key];
  }

  toggleOffcanvas() {
    this.offcanvasInstance?.toggle();
  }

  // =========================
  // SEARCH
  // =========================
  private norm(v: any): string {
    return String(v ?? '').trim().toLowerCase();
  }

  private matchesGlobalSearch(label: string): boolean {
    const q = this.norm(this.globalSearch);
    if (!q) return true;
    return this.norm(label).includes(q);
  }

  getFilteredOptions(key: DropdownKey): UiOption[] {
    const list =
      key === 'regioni' ? this.regioni :
      key === 'province' ? this.province :
      key === 'enti' ? this.enti :
      this.reti;

    const q = this.norm(this.globalSearch);
    if (!q) return list;

    return list.filter(o => this.matchesGlobalSearch(o.label));
  }

  hasFilteredResults(key: DropdownKey): boolean {
    return this.getFilteredOptions(key).length > 0;
  }

  highlight(label: string): string {
    const q = this.norm(this.globalSearch);
    if (!q) return label;

    return label.replace(
      new RegExp(`(${q})`, 'ig'),
      '<mark>$1</mark>'
    );
  }

  // =========================
  // SELECTIONS
  // =========================
  onToggleOption(key: DropdownKey, opt: UiOption, checked: boolean) {
    opt.selected = checked;

    switch (key) {
      case 'regioni': this.stateChange.toggleRegione(opt.id); break;
      case 'province': this.stateChange.toggleProvince(opt.id); break;
      case 'enti': this.stateChange.toggleEnti(opt.id); break;
      case 'reti': this.stateChange.toggleReti(opt.id); break;
    }
  }

  resetAll() {
    this.stateChange.resetAll();
    this.globalSearch = '';
  }

  // =========================
  // VM SYNC
  // =========================
  private setupVmListener() {
    combineLatest([
      this.stateChange.selectedRegioni$,
      this.stateChange.selectedProvince$,
      this.stateChange.selectedEnte$,
      this.stateChange.selectedRete$,
      this.stateChange.activeFiltersCount$,
      this.stateChange.dateRange$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([regioni, province, enti, reti, activeFiltersCount, dateRange]) => {
        this.vm = {
          regioni,
          province,
          enti,
          reti,
          activeFiltersCount,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        };
        this.syncSelectionsToUi();
      });
  }

  private toUiOptions(list: any[], key: DropdownKey): UiOption[] {
    return (list ?? []).map(x => {
      const label = String(x?.label ?? x?.nome ?? x?.id ?? x);
      const id = String(x?.id ?? label);
      return { id, label, selected: this.isSelectedInVm(key, id) };
    });
  }

  private isSelectedInVm(key: DropdownKey, id: string): boolean {
    switch (key) {
      case 'regioni': return this.vm.regioni.includes(id);
      case 'province': return this.vm.province.includes(id);
      case 'enti': return this.vm.enti.includes(id);
      case 'reti': return this.vm.reti.includes(id);
      default: return false;
    }
  }

  private syncSelectionsToUi() {
    this.regioni.forEach(o => o.selected = this.isSelectedInVm('regioni', o.id));
    this.province.forEach(o => o.selected = this.isSelectedInVm('province', o.id));
    this.enti.forEach(o => o.selected = this.isSelectedInVm('enti', o.id));
    this.reti.forEach(o => o.selected = this.isSelectedInVm('reti', o.id));
  }

  // =========================
  // DATE
  // =========================
  onStartDateChange(value: string) {
    this.stateChange.setDateRange(value, this.vm.endDate);
  }

  onEndDateChange(value: string) {
    this.stateChange.setDateRange(this.vm.startDate, value);
  }

  clearDateRange() {
    this.stateChange.setDateRange('', '');
  }

  ngOnDestroy(): void {
    this.offcanvasInstance?.dispose?.();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
